import { ChangeEvent } from 'react';

import { ImageFileParams } from '@/api/image';
import { TreatmentRequestLog } from '@/entities/diagnosis/libs';
import useDiagnosisAttachmentPreSignedUrls from '@/hooks/diagnosis/useDiagnosisAttachmentPreSignedUrls';
import useLogger from '@/hooks/useLogger';
import useModal from '@/hooks/useModal';
import { Medicine, SymptomImageFile, TreatmentRequest } from '@/models/diagnosis';
import { MODAL_BUTTON_TYPE, MODAL_TYPE } from '@/types/modal';
import { convertHeic, onCompressImage } from '@/utils/image';
import { debugReport } from '@/utils/report';

const MAX_SYMPTOM_IMAGE_COUNT = 4;

interface UseTelemedicineSymptomParams {
  treatmentRequest: TreatmentRequest;
  updateForm: (updater: (prev: any) => any) => void;
  detailSymptomInputRef: React.RefObject<HTMLTextAreaElement>;
  userTargetId?: string;
}

export const useTelemedicineSymptom = ({
  treatmentRequest,
  updateForm,
  detailSymptomInputRef,
  userTargetId,
}: UseTelemedicineSymptomParams) => {
  const logger = useLogger();
  const { showModal, closeModal } = useModal();
  const { mutatePresignedUrls, isPreSignedUrlsLoading } = useDiagnosisAttachmentPreSignedUrls();

  const updateSymptom = (symptom: string) => {
    const form = treatmentRequest.updateSymptom(symptom);
    updateForm(() => form);
  };

  const updateSymptomImages = (imageUrls: Array<SymptomImageFile>) => {
    const form = treatmentRequest.updateSymptomImages(imageUrls);
    updateForm(() => form);
  };

  const removeSymptomImage = (index: number) => {
    const form = treatmentRequest.removeSymptomImage(index);
    updateForm(() => form);
  };

  const uploadImageToS3 = async (files: Array<ImageFileParams>) => {
    const { success, imageFileList } = await mutatePresignedUrls({
      files,
    });

    if (success) {
      const symptomImages = imageFileList.map((image) => {
        return {
          fileUrl: image.preSignedUrl,
          thumbnailUrl: image.thumbnailUrl,
        };
      });
      updateSymptomImages(symptomImages);
    }
  };

  const onHandleChangeSymptomImage = async (e: ChangeEvent<HTMLInputElement>) => {
    try {
      if (e.target.files) {
        const attachedFile = Array.from(e.target.files);
        const enabledImageCount = MAX_SYMPTOM_IMAGE_COUNT - treatmentRequest.form.symptomInfo.attachFiles.length;
        if (enabledImageCount > 0) {
          const filteredImages = attachedFile.slice(0, enabledImageCount).filter((image) => {
            const isImage = image.type.includes('image');
            if (!isImage) {
              showModal({
                type: MODAL_TYPE.ALERT,
                props: {
                  title: '사진 파일을 올려주세요',
                  message: 'jpg, jpeg, png 확장자만 올릴 수 있어요.',
                  buttons: [
                    {
                      type: MODAL_BUTTON_TYPE.BOLD,
                      label: '확인',
                      onClick: () => {
                        closeModal();
                      },
                    },
                  ],
                },
              });
              return false;
            }
            return isImage;
          });

          const convertedImages = await Promise.all(
            filteredImages.map(async (image) => {
              if (image.type.includes('heic')) {
                return await convertHeic(image);
              }
              return image;
            })
          );

          const images = await Promise.all(
            convertedImages.map(async (image) => {
              return {
                origin: await onCompressImage(image, {
                  maxSizeMB: 5,
                }),
                thumbnail: await onCompressImage(image, {
                  maxSizeMB: 0.2,
                  maxWidthOrHeight: 300,
                }),
              };
            })
          );
          await uploadImageToS3(images);
          e.target.value = '';
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        debugReport(
          `진료신청서 > 사진 업로드 실패 userTargetId: ${userTargetId}`,
          {
            message: error.message,
          },
          error
        );
      }
    }
  };

  const handleMedicineSelect = (item: Medicine) => {
    logger.log({
      eventName: TreatmentRequestLog.RequestClickNonBenefitCategory,
    });
    treatmentRequest.updateTreatmentMedicineId(item.id);
    updateForm(() => treatmentRequest.getForm());
    detailSymptomInputRef.current?.scroll();
    detailSymptomInputRef.current?.focus();
  };

  return {
    isPreSignedUrlsLoading,
    updateSymptom,
    removeSymptomImage,
    onHandleChangeSymptomImage,
    handleMedicineSelect,
  };
};
